// UserAgreements.jsx

import React, { useState, useMemo } from "react";
import {
  Spinner,
  Dropdown,
  Button,
  Container,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import SubtleBadge from "components/common/SubtleBadge";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import IconButton from "components/common/IconButton";
import CustomPaginationFooter from "components/common/CustomPaginationFooter";
import { toast } from "react-toastify";
import useFormattedDate from "hooks/useFormatDate";

import useUserAgreements from "hooks/useUserAgreements";
// IMPORT THE NEW CUSTOM HOOK
import useAgreementStatusTransitions from "hooks/useAgreementStatusTransitions"; 

const UserAgreements = () => {
  const navigate = useNavigate();
  const { agreements, loading, refetch, updateAgreementStatus } = useUserAgreements();

  
  const { formatDateTime } = useFormattedDate();

  // --- NEW HOOK INTEGRATION ---
  // This hook encapsulates all the complex status change logic
  const { 
    showStatusModal, 
    setShowStatusModal, 
    statusChangeData,
    confirmStatusChange, 
    handleStatusChange,
    getStatusModalContent,
    getValidTransitions,
    isEditable,
  } = useAgreementStatusTransitions(updateAgreementStatus, refetch);


  // --- HANDLERS for Navigation & Deletion (Mostly unchanged) ---

  const handleViewAgreement = (id) => {
    navigate(paths.agreementDetails(id));
  };

  const handleEditAgreement = (id) => {
    navigate(paths.agreementEdit(id));
  };

  const handleCreateAgreement = () => {
    navigate(paths.agreementAdd);
  };

 

 


  // --- COLUMN DEFINITION (useMemo) ---

  const columns = useMemo(
    () => [
      {
        accessorKey: "version",
        header: "Version",
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "id",
        header: "Agreement ID",
        cell: ({ getValue }) => `${getValue().substring(0, 8)}...`,
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "createdAt",
        header: "Creation Date",
        cell: ({ getValue }) => formatDateTime(getValue()),
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const value = getValue();

          const statusMap = {
            ACTIVE: { type: "success", label: "Active" },
            DRAFT: { type: "secondary", label: "Draft" },
            READY: { type: "warning", label: "Ready" }, 
            EXPIRED: { type: "danger", label: "Expired" },
            ABANDONED: { type: "secondary", label: "Abandoned" },
            DELETED: { type: "danger", label: "Deleted" }
          };

          const fallback = { type: "secondary", label: "Unknown" };
          const { type, label } = statusMap[value] || fallback;

          return (
            <SubtleBadge pill bg={type}>
              {label}
            </SubtleBadge>
          );
        },
        meta: { headerProps: { className: "text-900" }, cellProps: { className: "fs-9" } },
      },
      {
        id: "actions",
        header: "Actions",
        meta: { headerProps: { className: "text-900" } },
        cell: ({ row }) => {
          const item = row.original;
          const currentStatus = item.status;
          
          const validTransitions = getValidTransitions(currentStatus);
          
          return (
            <Dropdown className="action-dropdown p-0">
              <Dropdown.Toggle
                as="button"
                className="bg-transparent border-0 p-0 shadow-none text-secondary"
              >
                ⋯
              </Dropdown.Toggle>
              <Dropdown.Menu className="action-menu">
                
                {/* --- Editing/Viewing --- */}
                {isEditable(currentStatus) && (
                    <Dropdown.Item onClick={() => handleEditAgreement(item.id)}>
                        Edit Content
                    </Dropdown.Item>
                )}
                <Dropdown.Item onClick={() => handleViewAgreement(item.id)}>
                    View Details
                </Dropdown.Item>

                <Dropdown.Divider />

                {/* --- Dynamic Status Transitions --- */}
                {validTransitions.length > 0 && (
                  <>
                    <Dropdown.Header className="pt-0 pb-1 ps-3">Change Status</Dropdown.Header>
                    {validTransitions.map((transition, index) => (
                        <Dropdown.Item 
                            key={index} 
                            onClick={() => confirmStatusChange(item.id, transition.targetStatus, currentStatus, item.version)}
                            className={transition.targetStatus === 'ACTIVE' ? 'text-success fw-bold' : ''}
                        >
                            {transition.label}
                        </Dropdown.Item>
                    ))}
                  </>
                )}
                
               
              </Dropdown.Menu>
            </Dropdown>
          );
        },
      },
    ],
    [formatDateTime, confirmStatusChange, handleEditAgreement, handleViewAgreement, getValidTransitions, isEditable] 
  );

  const table = useAdvanceTable({
    data: agreements,
    columns,
    sortable: true,
    perPage: 10,
    pagination: true,
  });

  if (loading && agreements.length === 0) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="primary" />
        <span
          className="ms-3 text-sm text-primary"
          style={{ fontSize: 13.33 }}
        >
          Loading User Agreements...
        </span>
      </Container>
    );
  }

  // Pass the stored version from state to the modal content helper
  const modalContent = statusChangeData.version 
    ? getStatusModalContent(statusChangeData.newStatus, statusChangeData.currentStatus, statusChangeData.version) 
    : {};

  return (
    <>
      <WidgetSectionTitle
        className="mt-4 mb-4 px-2 px-md-0"
        title="User Agreements"
        icon="handshake"
        subtitle="Manage all versions of Terms of Service, Privacy Policies, and other legal agreements."
      />

      <AdvanceTableProvider {...table}>
        <Row className="mb-3 g-3 px-2 px-md-0">
          <Col xs={12} md={6} lg={5}>
            <AdvanceTableSearchBox placeholder="Search agreements..." />
          </Col>
          <Col xs={12} md={6} lg={7} className="text-end">
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="plus"
              transform="shrink-3"
              onClick={handleCreateAgreement}
              className="w-100 w-md-auto"
            >
              <span className="d-none d-sm-inline-block ms-1">Add Agreement</span>
              <span className="d-sm-none">Add</span>
            </IconButton>
          </Col>
        </Row>

        <div className="px-2 px-md-0">
          <AdvanceTable
            headerClassName="bg-300 text-nowrap align-middle text-black"
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              bordered: true,
              striped: true,
              className: "fs-10 mb-0 overflow-hidden table-responsive",
              style: { minWidth: '600px' }
            }}
          />
          <CustomPaginationFooter
            table={table}
            totalEntries={agreements.length}
            loading={loading}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>

      
      {/* Status Change Modal (Uses logic from the hook) */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>{modalContent.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {modalContent.body}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
                    Cancel
                </Button>
                <Button 
                    variant={modalContent.confirmVariant} 
                    onClick={() => handleStatusChange(statusChangeData.id, statusChangeData.newStatus)}
                >
                    {modalContent.confirmLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    </>
  );
};

export default UserAgreements;